import * as React from 'react';
import { useState } from 'react';

import { ActivityType } from '@showbie-socrative/socrative-utils';

import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/standard';
import { Link } from '~shared/components/Link';
import { ExportReports } from '~teacher/components/ExportReports';
import { PaywallProvider } from '~teacher/components/PaywallDialog';
import { ResultsHeader } from '~teacher/components/ResultsHeader';
import {
  ShareResults,
  ShareResultsPaywall,
} from '~teacher/components/ShareResults';
import { useIsQuickQuestion } from '~teacher/hook';
import { ActivitySettingsCamelCase } from '~teacher/models/activities';
import { proCheck } from '~teacher/util/proCheck';

import { QuizResultsHeaderContainer } from './QuizResultsHeaderContainer';

interface Props {
  title: string;
  quizStandard?: string;
  startDate: string;
  endDate: string;
  activityInstanceId?: number;
  isSaveToDriveDisabled?: boolean;
  activityType: ActivityType;
  activitySettings: ActivitySettingsCamelCase;
}

export function QuizResultsHeaderFinished({
  activityInstanceId,
  isSaveToDriveDisabled,
  activityType,
  title,
  quizStandard,
  startDate,
  endDate,
  activitySettings,
}: Props) {
  const isQuickQuestion = useIsQuickQuestion(activityType);
  const canShareResults = proCheck.canShareResults();

  const [showExportReportsModal, setShowExportReportsModal] = useState(false);
  const [showEmailStudentsModal, setShowEmailStudentsModal] = useState(false);
  const [showPaywallDialog, setShowPaywallDialog] = useState(false);

  const handleEmailStudentsClicked = () => {
    if (canShareResults) {
      setShowEmailStudentsModal(true);
    } else {
      setShowPaywallDialog(true);
    }
  };

  return (
    <PaywallProvider>
      <QuizResultsHeaderContainer>
        <div className="flex flex-row">
          <Link
            href="#reports"
            className="text-sm font-sans leading-8 font-600 text-cyan-700 hocus:text-cyan-600 group relative no-underline"
          >
            <div className="flex items-center">
              <IconChevron direction="left" />
              {translate('Reports')}
            </div>
          </Link>
        </div>
        <ResultsHeader
          title={title}
          standard={quizStandard}
          startDate={startDate}
          endDate={endDate}
          activitySettings={activitySettings}
        >
          <SmallButton
            appearance="primary"
            isDisabled={isQuickQuestion}
            onClick={handleEmailStudentsClicked}
            className="flex-grow max-w-80"
            data-testid="share"
          >
            {translate('Email Results to Students')}
          </SmallButton>
          <SmallButton
            appearance="primary"
            isDisabled={activityType === ActivityType.NoReport}
            className="flex-grow max-w-80"
            data-testid="export"
            onClick={() => setShowExportReportsModal(true)}
          >
            {translate('Export Results')}
          </SmallButton>
        </ResultsHeader>
      </QuizResultsHeaderContainer>
      <ShareResultsPaywall
        open={showPaywallDialog}
        onClose={() => setShowPaywallDialog(false)}
      />

      <ShareResults
        open={showEmailStudentsModal}
        onClose={() => setShowEmailStudentsModal(false)}
        activityInstanceId={activityInstanceId}
      />

      <ExportReports
        open={showExportReportsModal}
        onClose={() => setShowExportReportsModal(false)}
        activityInstanceId={activityInstanceId}
        activityType={activityType}
        isSaveToDriveDisabled={isSaveToDriveDisabled}
      />
    </PaywallProvider>
  );
}
